/**
 * /apps/:slug/usage — per-app usage telemetry.
 *
 * Visible to anyone who can see the app (managers, app-role holders, or
 * any Mytra employee on a workspace-visible app). Each "request" is an
 * authenticated call through the SDK rather than a literal page view;
 * the UI copy is honest about that.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import type { AppUsageResponse, UsageRange } from '../api/types.js';
import { AppTabs } from '../components/AppTabs.js';
import { IconBolt, IconInfo, IconUsers } from '../components/Icons.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { Sparkline, UsageChart } from '../components/UsageChart.js';
import {
  Banner,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  PageSkeleton,
} from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith } from '../lib/useAppPage.js';

const RANGE_OPTIONS: { value: UsageRange; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: '90d', label: '90 days' },
];

export function Usage() {
  const { slug } = useParams();
  const [range, setRange] = useState<UsageRange>('week');
  const {
    app,
    payload: usage,
    loading,
    error,
  } = useAppPageWith(
    slug,
    (s) => api.get<AppUsageResponse>(`/api/apps/${encodeURIComponent(s)}/usage?range=${range}`),
    [range],
  );

  if (loading) return <PageSkeleton rows={4} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !usage) return null;

  if (!app.is_manager) {
    return (
      <ManagerOnlyPage
        app={app}
        title="Usage"
        description="Per-app usage telemetry — managers only."
      />
    );
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Usage"
        description="Who's using this app and how often. Counts every authenticated request through the Kin SDK — for a typical web app that's one event per page load plus one per API call."
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0">
          <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
            <RangePicker value={range} onChange={setRange} />
            <p className="text-xs text-faint inline-flex items-center gap-1.5">
              <IconInfo size={12} />
              Updates roughly once a minute.
            </p>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
            <Card>
              <div className="p-5">
                <div className="text-xs uppercase tracking-wider text-muted font-medium inline-flex items-center gap-1.5">
                  <IconBolt size={12} /> Authenticated requests
                </div>
                <div className="font-display text-4xl font-semibold tabular text-ink mt-2">
                  {usage.totals.events.toLocaleString()}
                </div>
                <div className="text-xs text-muted mt-1">in the selected range</div>
              </div>
            </Card>
            <Card>
              <div className="p-5">
                <div className="text-xs uppercase tracking-wider text-muted font-medium inline-flex items-center gap-1.5">
                  <IconUsers size={12} /> Unique users
                </div>
                <div className="font-display text-4xl font-semibold tabular text-ink mt-2">
                  {usage.totals.uniqueUsers.toLocaleString()}
                </div>
                <div className="text-xs text-muted mt-1">distinct people who used the app</div>
              </div>
            </Card>
          </div>

          {/* Time-series chart */}
          <Card className="mb-5">
            <CardHeader
              title="Activity over time"
              description={
                range === 'day' ? 'Hourly buckets, last 24 hours' : 'Daily buckets across the range'
              }
            />
            <div className="p-5 pt-3">
              <UsageChart series={usage.series} range={range} height={200} />
            </div>
          </Card>

          {/* Per-user table */}
          <Card>
            <CardHeader
              title="Who's using it"
              description={`${usage.users.length} user${usage.users.length === 1 ? '' : 's'} in this range`}
            />
            {usage.users.length === 0 ? (
              <div className="px-5 pb-5">
                <EmptyState
                  icon={<IconUsers size={18} />}
                  title="Nobody yet"
                  description="Once someone authenticates against this app via the Kin SDK, they'll show up here."
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-muted font-medium">
                      <th className="text-left font-medium px-5 py-2.5">Person</th>
                      <th className="text-right font-medium px-3 py-2.5 hidden sm:table-cell">
                        First seen
                      </th>
                      <th className="text-right font-medium px-3 py-2.5">Last seen</th>
                      <th className="text-right font-medium px-3 py-2.5 tabular">Requests</th>
                      <th className="text-right font-medium px-5 py-2.5 hidden md:table-cell">
                        Activity
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.users.map((u) => (
                      <tr key={u.user_id} className="border-t border-line">
                        <td className="px-5 py-2.5 min-w-0">
                          <div className="font-medium text-ink truncate">
                            {u.name ?? u.email ?? u.user_id}
                          </div>
                          {u.name && u.email && (
                            <div className="text-xs text-muted truncate">{u.email}</div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs text-muted hidden sm:table-cell">
                          {relTime(u.first_seen_at)}
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs text-muted">
                          {relTime(u.last_seen_at)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular font-medium text-ink">
                          {u.events.toLocaleString()}
                        </td>
                        <td className="px-5 py-2.5 text-right hidden md:table-cell">
                          <Sparkline values={u.sparkline} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </main>
      </div>
    </div>
  );
}

function RangePicker({
  value,
  onChange,
}: {
  value: UsageRange;
  onChange: (next: UsageRange) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Time range"
      className="inline-flex items-center gap-1 p-1 rounded-lg bg-surface-2 border border-line"
    >
      {RANGE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          role="tab"
          aria-selected={value === opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={
            'px-3 py-1 rounded-md text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 ' +
            (value === opt.value ? 'bg-surface text-ink shadow-card' : 'text-muted hover:text-ink')
          }
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
